import React, { FC } from 'react';
import { Route, Switch } from 'react-router-dom';
import { MenuLink } from '../../shared/config';
import { Header, Sidenav, SidenavItem, SupportAgentBanner, UserRoute } from '../components';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { CartComplete } from '../components/payment/Complete';
import {
  EditAccountComponent,
  EditPersonalComponent,
  OrderHistoryComponent,
} from '../containers/account';
import { launder, useTranslation } from '../translations';
import { useConfig, useConvention, useUser } from '../utils';
import { currencyIcon } from '../utils/currencyIcon';
import { LazySecondFactor } from './account/secondFactor/lazy';
import { SocialLinks } from './account/sociallinks';
import { VolunteerComponent } from './apply';
import { ApplyDealerArea } from './apply/dealer/DealerArea';
import { Changelog } from './changelog';
import { ChildRegistrationComponent } from './convention/children';
import { Dashboard } from './dashboard';
import { NotFoundError } from './error/notfound';
import { HotelRouteHandler } from './hotel';
import { OAuthAuthorize, OAuthLogoutComponent } from './oauth';
import { OrderTickets } from './order/tickets';
import { Privacy } from './policy/privacy';
import { TermsComponent } from './policy/terms';
import { VendorPolicies } from './policy/vendor';
import { ProductPageHOC } from './store/productPageHOC';
import { Cart, LazyOrderForm, RegistrationWrapper, Store } from '.';

export const RenderMenuLinks: FC<{
  readonly menuLinks: MenuLink[];
  readonly type: string;
}> = ({ menuLinks, type }) => {
  const user = useUser();

  return menuLinks
    .filter((t) => t.type === type)
    .map((t) => {
      t.url = t.url
        .replaceAll('{{user.id}}', user?.id.toString() || '')
        .replaceAll('{{user.username}}', encodeURIComponent(user?.username || ''));

      return t;
    })
    .map((t) => (
      <SidenavItem
        icon={t.icon}
        key={t.name}
        link={t.url}
        newTab={t.newTab}
        requireLoggedIn
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        text={launder(t.name)}
      />
    ));
};

export const CoreContainer: FC = () => {
  const { ts } = useTranslation();
  const config = useConfig();
  const convention = useConvention();

  return (
    <ErrorBoundary>
      <Sidenav>
        <SidenavItem icon="shopping_cart" id="navCheckout" link="/cart" text="common.checkout" />

        <SidenavItem header requireLoggedIn text="common.dashboard" />
        <SidenavItem
          icon="supervised_user_circle"
          id="navHousekeeping"
          link="/housekeeping"
          requiredPermissions="system:login"
          text="common.housekeeping"
        />
        <SidenavItem icon="dashboard" id="navDashboard" link="/" text="common.dashboard" />

        <SidenavItem header requireLoggedIn text="account_management" />
        <SidenavItem
          icon="portrait"
          id="navAccountDetails"
          link="/account/login"
          requireLoggedIn
          text="edit_account_details"
        />
        <SidenavItem
          icon="security"
          id="navSecondFactor"
          link="/account/2fa"
          requireLoggedIn
          text="second_factor"
        />
        <SidenavItem
          icon="portrait"
          id="navPersonalDetails"
          link="/account/personal"
          requireLoggedIn
          text="common.edit_personal_details"
        />
        {config.socialLinks && (
          <SidenavItem
            icon="extension"
            id="navExternalApplications"
            link="/account/sociallinks"
            requireLoggedIn
            text="social_links"
          />
        )}
        <SidenavItem
          icon="history"
          id="navOrderHistory"
          link="/account/orders"
          requireLoggedIn
          text="common.order_history"
        />

        <SidenavItem header requireLoggedIn text="attendance" />
        <SidenavItem
          icon="how_to_reg"
          id="navConRegistration"
          link="/event/register"
          requireLoggedIn
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          text={launder(ts('register_for', convention.shortName))}
        />
        {convention.hotelsEnabled && (
          <SidenavItem
            icon="hotel"
            id="navHotelReservations"
            link="/hotel"
            matchUrlPrefix
            requireLoggedIn
            text="hotel_reservations"
          />
        )}
        {convention.maxChildren > 0 && (
          <SidenavItem
            icon="person_add"
            id="navChildRegistration"
            link="/convention/children"
            requireLoggedIn
            text="register_children"
          />
        )}
        <RenderMenuLinks menuLinks={config.menuLinks} type="attendance" />
        {convention.enableEventStore && (
          <SidenavItem
            icon={currencyIcon(config.currencySymbol)}
            id="navEventStore"
            link="/store"
            requireLoggedIn
            text="event_store"
          />
        )}
        <ApplicationSelection />
      </Sidenav>
      <div className="page-wrap">
        <Header />
        <SupportAgentBanner />
        <Switch>
          <Route component={TermsComponent} exact path="/policy/terms" />
          <Route component={Privacy} exact path="/policy/privacy" />
          <Route component={VendorPolicies} exact path="/policy/vendor" />
          <UserRoute component={OAuthAuthorize} exact path="/oauth/authorize" />
          <UserRoute component={OAuthLogoutComponent} exact noRedirect path="/oauth/logout" />
          <UserRoute component={EditAccountComponent} exact path="/account/login" />
          <UserRoute component={LazySecondFactor} exact path="/account/2fa" />
          <UserRoute component={EditPersonalComponent} exact path="/account/personal" />
          <UserRoute component={SocialLinks} exact path="/account/sociallinks" />
          <UserRoute component={OrderHistoryComponent} exact path="/account/orders" />
          <UserRoute component={OrderTickets} exact path="/order/:id/tickets" />
          <UserRoute component={Dashboard} exact path="/" />
          <UserRoute component={Cart} exact path="/cart" />
          <UserRoute component={CartComplete} exact path="/cart/complete/:id" />
          <UserRoute component={Changelog} exact path="/changelog" />
          <UserRoute component={ProductPageHOC} exact path="/store/product/:id" />
          <UserRoute component={Store} path="/store" />
          <UserRoute component={ChildRegistrationComponent} exact path="/convention/children" />
          <UserRoute component={RegistrationWrapper} path="/event" />
          <UserRoute component={VolunteerComponent} exact path="/apply/volunteer" />
          <UserRoute component={ApplyDealerArea} path="/vendor" />
          <UserRoute component={LazyOrderForm} exact path="/pay" />
          <HotelRouteHandler />
          <Route component={NotFoundError} />
        </Switch>
      </div>
    </ErrorBoundary>
  );
};

const ApplicationSelection: FC = () => {
  const config = useConfig();
  const { enableVolunteers, enableDealers } = useConvention();
  return (
    <>
      <SidenavItem header requireLoggedIn text="applications" />
      {enableVolunteers && (
        <SidenavItem
          icon="people"
          id="navVolunteering"
          key="navVolunteering"
          link="/apply/volunteer"
          requireLoggedIn
          text="volunteering"
        />
      )}
      <RenderMenuLinks key="menuLinks" menuLinks={config.menuLinks} type="application" />
      {enableDealers && (
        <SidenavItem
          icon={currencyIcon(config.currencySymbol)}
          id="navDealersArea"
          key="navDealersArea"
          link="/vendor"
          requireLoggedIn
          text="vendor"
        />
      )}
    </>
  );
};
